/* eslint-disable */
import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import { Link } from "react-router-dom";

/* import url and gateway methods */
import { getData } from "../../../utils/Gateway";
import * as url from "../../../urlhelpers/UrlHelper";

// MRT
import { MaterialReactTable } from "material-react-table";
import * as XLSX from "xlsx";

// import context
import { GlobalProvider } from "context/GlobalContext";

// import images
import { assetImages } from "constants";

// import translation function for language translation
import { useTranslation } from "react-i18next";
import useAppMRT from "hooks/useAppMRT";

// ─────────────────────────────────────────────────────────────────────────────
// Map Kendo Grid field names → the exact query param name the backend expects.
// Add any new column here if you add more GridColumns in future.
// ─────────────────────────────────────────────────────────────────────────────
const FIELD_TO_PARAM = {
  username:    "username",    // maps to req.query.username  → searches name/surname
  usersurname: "usersurname", // maps to req.query.usersurname
  email:       "email",       // maps to req.query.email
  phone:       "phone",       // maps to req.query.phone
  companydata: "companydata", // maps to req.query.companydata
  "role.name": "rolename",    // maps to req.query.rolename  (role is a nested field)
  tagstring:   "tagstring",   // maps to req.query.tagstring (computed field)
  city:        "city",        // maps to req.query.city
  state:       "state",       // maps to req.query.state
  country:     "country",     // maps to req.query.country
  zipcode:     "zipcode",     // maps to req.query.zipcode
};

const UserListArea = ({
  downloadExcel,
  search,
  setRecords,
  checkedRecordCount,
}) => {
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const { t } = useTranslation();
  const token = localStorage.getItem("token");
  const appMRTProps = useAppMRT();

  const {
    isUserListLoading,
    setIsUserListLoading,
    showUserList,
    setShowUserList,
    setEditUserData,
    setCopyUserData,
    showUserNameColumn,
    showUserSurnameColumn,
    showUserRoleColumn,
    showUserEmailColumn,
    showUserTagColumn,
    showCompanyColumn,
    showUserPhoneColumn,
    showUserCityColumn,
    showUserZipcodeColumn = true,
    showUserStateColumn,
    showUserCountryColumn,
    searchUserRoles,
    searchUserTags,
    searchUserStatus,
    searchUserZipcodes,
    searchUserCityList,
    searchUserStateList,
    searchUserCountryList,
    screenHeight,
    searchUserIsCompany,
  } = useContext(GlobalProvider);

  // ── State ────────────────────────────────────────────────────────────────────
  const [userData, setUserData] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const debounceTimer = useRef(null);
  const columnFilterTimer = useRef(null);

  // ── Build request URL ────────────────────────────────────────────────────────
  // All filtering goes through here — sidebar filters, top search bar, and column filters.
  const buildRequestUrl = (currentSearch = search, currentColumnFilters = columnFilters) => {
    let requestUrl =
      url.API_BASEURL + url.API_GET_ALL_USER + `?token=${token}&limit=100`;

    // ── Sidebar / context filters ──────────────────────────────────────────
    if (searchUserRoles.length > 0)       requestUrl += `&userrole=${searchUserRoles}`;
    if (searchUserTags.length > 0)        requestUrl += `&usertag=${searchUserTags}`;
    if (searchUserStatus.length > 0)      requestUrl += `&userstatus=${searchUserStatus}`;
    if (searchUserZipcodes.length > 0)    requestUrl += `&userzipcode=${searchUserZipcodes}`;
    if (searchUserCityList.length > 0)    requestUrl += `&usercity=${searchUserCityList}`;
    if (searchUserStateList.length > 0)   requestUrl += `&userstate=${searchUserStateList}`;
    if (searchUserCountryList.length > 0) requestUrl += `&usercountry=${searchUserCountryList}`;
    if (searchUserIsCompany)              requestUrl += `&useriscompany=${searchUserIsCompany}`;

    // ── Top search bar (global search across all fields) ───────────────────
    if (currentSearch?.trim()) {
      requestUrl += `&globalsearch=${encodeURIComponent(currentSearch.trim())}`;
    }

    // ── Column filters (each maps to its own API param via FIELD_TO_PARAM) ─
    const activeColumnFilters = currentColumnFilters.filter(
      ({ id, value }) => FIELD_TO_PARAM[id] && value?.toString().trim()
    );
    if (activeColumnFilters.length > 0) {
      requestUrl += `&filter_op=and`;
      activeColumnFilters.forEach(({ id, value }) => {
        requestUrl += `&${FIELD_TO_PARAM[id]}=${encodeURIComponent(value.toString().trim())}`;
      });
    }

    return requestUrl;
  };

  // ── Main fetch ───────────────────────────────────────────────────────────────
  const getAllUserList = async (currentSearch = search, currentColumnFilters = columnFilters) => {
    if (!["admin", "manager"].includes(userInfo?.role?.name)) {
      setUserData([]);
      return;
    }

    try {
      setIsUserListLoading(true);

      const requestUrl = buildRequestUrl(currentSearch, currentColumnFilters);
      console.log("user list url========>", requestUrl);

      const response = await getData(requestUrl);
      console.log("user list response------>", response);

      if (response.status) {
        setUserData(response.data);
        setRecords(response.data.length);
      }
    } catch (error) {
      console.error(error.message);
    } finally {
      setIsUserListLoading(false);
    }
  };

  // ── Initial load ─────────────────────────────────────────────────────────────
  useEffect(() => {
    getAllUserList();
  }, []);

  // ── Top search bar ───────────────────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      getAllUserList(search);
    }, 500);
  }, [search]);

  // ── Sidebar context filter changes ───────────────────────────────────────────
  useEffect(() => {
    if (
      searchUserRoles.length > 0 ||
      searchUserTags.length > 0 ||
      searchUserStatus.length > 0 ||
      searchUserZipcodes.length > 0 ||
      searchUserCityList.length > 0 ||
      searchUserStateList.length > 0 ||
      searchUserCountryList.length > 0 ||
      searchUserIsCompany
    ) {
      getAllUserList();
    }
  }, [
    searchUserRoles,
    searchUserTags,
    searchUserStatus,
    searchUserZipcodes,
    searchUserCityList,
    searchUserStateList,
    searchUserCountryList,
    searchUserIsCompany,
  ]);

  // ── Column filter changes (debounced, API-driven) ────────────────────────────
  useEffect(() => {
    clearTimeout(columnFilterTimer.current);
    columnFilterTimer.current = setTimeout(() => {
      getAllUserList(search, columnFilters);
    }, 500);
  }, [columnFilters]);

  // ── Refresh trigger from parent (e.g. after save / delete) ───────────────────
  useEffect(() => {
    if (showUserList) {
      getAllUserList();
      setShowUserList(false);
    }
  }, [showUserList]);

  // ── Get selected rows ────────────────────────────────────────────────────────
  const getSelectedData = () => {
    const selectedIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
    return userData.filter((user) => selectedIds.includes(user._id));
  };

  // ── Excel export ─────────────────────────────────────────────────────────────
  const exportExcel = () => {
    const selectedData = getSelectedData();
    const exportData = selectedData.length > 0 ? selectedData : userData;
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "User Management.xlsx");
  };
  useEffect(() => {
    downloadExcel && exportExcel();
  }, [downloadExcel]);

  // ── Sync selected count to parent ────────────────────────────────────────────
  useEffect(() => {
    const selectedCount = Object.keys(rowSelection).filter((key) => rowSelection[key]).length;
    checkedRecordCount(selectedCount);
  }, [rowSelection, userData]);

  // ── Custom cell renderers ─────────────────────────────────────────────────────
  const nameHtml = ({ dataItem }) => (
    <div className="kendoCustomColumn overflow-visible">
      <div className="d-flex align-items-center gap-2">
        <p className="d-flex gap-2 align-items-center text-nowrap">
          <span className="d-block icon-user">
            <img
              src={
                dataItem?.photoimage
                  ? url.SERVER_URL + dataItem?.photoimage?.path
                  : assetImages?.defaultUser
              }
              alt=""
            />
          </span>
          <span
            className="d-block"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            title={`${dataItem.username} ${dataItem.usersurname}`}
          >
            {dataItem.username}
          </span>
        </p>

        <div className="ms-auto d-flex align-items-center gap-1">
          <Link to="#">
            <span
              className={`d-block material-symbols-outlined icon-md ${
                dataItem.allowsendingmail ? "text-green" : "text-danger"
              }`}
            >
              mail
            </span>
          </Link>

          {dataItem?.iscompany ? (
            <Link to="#">
              <span className="d-block material-symbols-outlined icon-md text-green">
                business
              </span>
            </Link>
          ) : (
            <Link to="#">
              <span
                className={`d-block material-symbols-outlined icon-md ${
                  dataItem.zipcode ? "text-green" : "text-danger"
                }`}
              >
                location_on
              </span>
            </Link>
          )}

          <div className="dropdown cell-dropdown mt-2">
            <button
              className="bg-transparent p-0 border-0"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <span className="d-block material-symbols-outlined">
                more_vert
              </span>
            </button>
            <ul className="dropdown-menu border-0 shadow-lg">
              <li>
                <Link
                  className="dropdown-item"
                  to="#saveUserPopup"
                  data-bs-toggle="offcanvas"
                  onClick={() => setEditUserData(dataItem._id)}
                >
                  {t("Edit")}
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item"
                  to={`/profile/${dataItem._id}`}
                >
                  {t("View Profile")}
                </Link>
              </li>
              <li>
                <Link
                  className="dropdown-item"
                  to="#saveUserPopup"
                  data-bs-toggle="offcanvas"
                  onClick={() => setCopyUserData(dataItem._id)}
                >
                  {t("Copy User")}
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const tableColumns = useMemo(() => {
    const cols = [];

    if (showUserNameColumn) {
      cols.push({
        id: "username",
        accessorKey: "username",
        header: t("Name"),
        Cell: ({ row }) => nameHtml({ dataItem: row.original }),
        size: 400,
        muiTableBodyCellProps: {
          sx: { overflow: "visible", position: "static" },
        },
      });
    }
    if (showUserSurnameColumn) {
      cols.push({
        id: "usersurname",
        accessorKey: "usersurname",
        header: t("Surname"),
        size: 400,
      });
    }
    if (showUserEmailColumn) {
      cols.push({
        id: "email",
        accessorKey: "email",
        header: t("Email"),
        size: 400,
      });
    }
    if (showCompanyColumn) {
      cols.push({
        id: "companydata",
        accessorKey: "companydata",
        header: t("Company"),
        size: 300,
      });
    }
    if (showUserRoleColumn) {
      cols.push({
        id: "role.name",
        accessorFn: (row) => row.role?.name ?? "",
        header: t("Role"),
        size: 300,
      });
    }
    if (showUserTagColumn) {
      cols.push({
        id: "tagstring",
        accessorKey: "tagstring",
        header: t("Tags"),
        size: 250,
      });
    }
    if (showUserPhoneColumn) {
      cols.push({
        id: "phone",
        accessorKey: "phone",
        header: t("Phone"),
        size: 250,
      });
    }
    if (showUserCityColumn) {
      cols.push({
        id: "city",
        accessorKey: "city",
        header: t("City"),
        size: 250,
      });
    }
    if (showUserZipcodeColumn) {
      cols.push({
        id: "zipcode",
        accessorKey: "zipcode",
        header: t("Zip Code"),
        size: 250,
      });
    }
    if (showUserStateColumn) {
      cols.push({
        id: "state",
        accessorKey: "state",
        header: t("State"),
        size: 250,
      });
    }
    if (showUserCountryColumn) {
      cols.push({
        id: "country",
        accessorKey: "country",
        header: t("Country"),
        size: 250,
      });
    }

    return cols;
  }, [
    showUserNameColumn,
    showUserSurnameColumn,
    showUserRoleColumn,
    showUserEmailColumn,
    showUserTagColumn,
    showCompanyColumn,
    showUserPhoneColumn,
    showUserCityColumn,
    showUserZipcodeColumn,
    showUserStateColumn,
    showUserCountryColumn,
    userData,
  ]);

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="table-responsive">
      {/* Hidden checkboxes so AddLabel can detect selected rows via filtername="selectedrecord" */}
      <div style={{ display: "none" }}>
        {userData.map((user) => (
          <input
            key={user._id}
            type="checkbox"
            filtername="selectedrecord"
            value={user._id}
            checked={!!rowSelection[user._id]}
            onChange={() => {}}
          />
        ))}
      </div>
      <MaterialReactTable
        {...appMRTProps}
        columns={tableColumns}
        data={userData}
        enableSorting
        enableFilters
        enableGlobalFilter={false}
        manualFiltering
        enableGrouping
        enableColumnOrdering
        enableStickyHeader
        enableRowSelection
        onRowSelectionChange={setRowSelection}
        onColumnFiltersChange={setColumnFilters}
        state={{ rowSelection, columnFilters, isLoading: isUserListLoading }}
        muiCircularProgressProps={{ style: { display: "none" } }}
        getRowId={(row) => row._id}
        muiTableContainerProps={{ sx: { maxHeight: screenHeight, minHeight: "300px" } }}
        muiTableBodyRowProps={{ sx: { overflow: "visible" } }}
        muiTableBodyProps={{ sx: { overflow: "visible" } }}
        initialState={{
          pagination: { pageSize: 100, pageIndex: 0 },
          showColumnFilters: true,
        }}
      />
    </div>
  );
};

export default UserListArea;
